import type {
  FooterComponent,
  HeaderComponent,
  HeaderFooterComponent,
  PositionComponent,
} from "../api/ProjectAPI.types";
import type {
  DispositionState,
  ItemDisposition,
} from "../pages/platform/components/headerFooter/Disposition";

export const headerFooterComponentListApiToDispositionState = <
  T extends HeaderComponent | FooterComponent,
>(
  components: T[],
  availableElements: ItemDisposition[],
): DispositionState => {
  const currentItems = components.reduce(
    (acc: DispositionState, component) => {
      const element = availableElements.find(
        (headerElement) => headerElement.id === component.type,
      );

      if (!element) return acc;

      const updatedElement = {
        ...element,
        rank: component.rank,
        componentUuid: component.uuid,
        params: {
          ...element.params,
          ...component.params,
        },
      };

      if (!component.enabled) {
        acc.available = acc.available.map((item) =>
          item.id === updatedElement.id ? updatedElement : item,
        );
        return acc;
      }

      if (component.params.position === "left") {
        acc.left.push(updatedElement);
        acc.available = acc.available.filter(
          (item) => item.id !== updatedElement.id,
        );
      }

      if (component.params.position === "right") {
        acc.right.push(updatedElement);
        acc.available = acc.available.filter(
          (item) => item.id !== updatedElement.id,
        );
      }

      if (component.params.position === "center") {
        acc.center.push(updatedElement);
        acc.available = acc.available.filter(
          (item) => item.id !== updatedElement.id,
        );
      }

      return acc;
    },
    {
      available: availableElements,
      left: [],
      center: [],
      right: [],
    },
  );

  return currentItems;
};

export const headerFooterComponentListDispositionStateToApi = (
  dispositionState: DispositionState,
  initialDispositionState: DispositionState,
  section: "header" | "footer",
): {
  added: Omit<HeaderFooterComponent, "uuid">[];
  updated: HeaderFooterComponent[];
} => {
  const placedZones = ["left", "center", "right"] as const;

  interface PositionEntry {
    zone: PositionComponent;
    item: ItemDisposition;
  }

  const currentPositions = placedZones.reduce<Record<string, PositionEntry>>(
    (acc, zone) => {
      dispositionState[zone].forEach((item) => {
        acc[item.id] = { zone, item };
      });
      return acc;
    },
    {},
  );

  const initialPositions = placedZones.reduce<Record<string, PositionEntry>>(
    (acc, zone) => {
      initialDispositionState[zone].forEach((item) => {
        acc[item.id] = { zone, item };
      });
      return acc;
    },
    {},
  );

  const added: Omit<HeaderFooterComponent, "uuid">[] = [];
  const updated: HeaderFooterComponent[] = [];

  Object.entries(currentPositions).forEach(([id, { zone, item }]) => {
    if (!(id in initialPositions)) {
      if (item.componentUuid) {
        const itemToUpdate = {
          section,
          params: {
            ...item.params,
            position: zone,
          },
          enabled: true,
          rank: item.rank,
          type: item.id,
          dynamic_component: null,
          label: null,
          platform: null,
          static_component: null,
          uuid: item.componentUuid,
        } as HeaderFooterComponent;
        updated.push(itemToUpdate);
      } else {
        const itemToAdd = {
          section,
          params: {
            ...item.params,
            position: zone,
          },
          enabled: true,
          rank: item.rank,
          type: item.id,
          dynamic_component: null,
          label: null,
          platform: null,
          static_component: null,
        };
        added.push(itemToAdd);
      }
    } else if (
      initialPositions[id] &&
      (initialPositions[id].zone !== zone ||
        initialPositions[id].item.rank !== item.rank)
    ) {
      const itemToUpdate = {
        section,
        params: {
          ...item.params,
          position: zone,
        },
        enabled: true,
        rank: item.rank,
        type: item.id,
        dynamic_component: null,
        label: null,
        platform: null,
        static_component: null,
        uuid: item.componentUuid,
      } as HeaderFooterComponent;
      updated.push(itemToUpdate);
    }
  });

  Object.entries(initialPositions).forEach(([id, { item }]) => {
    if (!(id in currentPositions) && item.componentUuid) {
      const itemToUpdate = {
        section,
        params: item.params,
        enabled: false,
        rank: item.rank,
        type: item.id,
        dynamic_component: null,
        label: null,
        platform: null,
        static_component: null,
        uuid: item.componentUuid,
      } as HeaderFooterComponent;
      updated.push(itemToUpdate);
    }
  });

  return { added, updated };
};
